import { Box, Typography, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useState, useEffect } from 'react';

interface BrandingFooterProps {
  variant?: 'default' | 'compact';
  sx?: SxProps<Theme>;
  statusMessage?: string | null;
  statusTimestamp?: Date | null;
}

export function BrandingFooter({ variant = 'default', sx, statusMessage, statusTimestamp }: BrandingFooterProps) {
  const theme = useTheme();
  const [timeAgo, setTimeAgo] = useState<string>('');

  const fontSize = variant === 'compact' ? '11px' : '12px';
  const padding = variant === 'compact' ? '4px 16px' : '6px 16px';

  useEffect(() => {
    if (!statusTimestamp) {
      setTimeAgo('');
      return;
    }

    const updateTimeAgo = () => {
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - statusTimestamp.getTime()) / 1000);

      if (diffInSeconds < 60) {
        setTimeAgo('less than min');
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        if (minutes === 1) {
          setTimeAgo('min');
        } else {
          setTimeAgo(`${minutes}mins`);
        }
      } else {
        const hours = Math.floor(diffInSeconds / 3600);
        setTimeAgo(`${hours}h`);
      }
    };

    // Update immediately
    updateTimeAgo();

    // Update every second
    const interval = setInterval(updateTimeAgo, 1000);

    return () => clearInterval(interval);
  }, [statusTimestamp]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding,
        fontSize,
        color: 'text.secondary',
        fontFamily: theme.typography.fontFamily,
        width: '100%',
        boxSizing: 'border-box',
        flexShrink: 0,
        // Responsive: reduce font size on mobile (< 768px)
        '@media (max-width: 767px)': {
          fontSize: '10px',
          padding: variant === 'compact' ? '4px 12px' : '6px 12px',
        },
        ...sx,
      }}
    >
      {/* Left side - Status message */}
      <Box sx={{ flex: 1, textAlign: 'left' }}>
        {statusMessage && timeAgo && (
          <Typography
            component="span"
            sx={{
              fontSize: 'inherit',
              color: 'text.secondary',
              fontStyle: 'normal',
            }}
          >
            Timetable last synced {timeAgo} ago
          </Typography>
        )}
      </Box>

      {/* Right side - Branding */}
      <Box sx={{ textAlign: 'right' }}>
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            color: 'inherit',
          }}
        >
          Powered by{' '}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            fontWeight: 'bold',
            color: 'text.primary',
          }}
        >
          Santanix Digital Lab
        </Typography>
      </Box>
    </Box>
  );
}
